import type {
  ApiOnProgress,
  ApiParsedMedia,
  ApiPreparedMedia,
} from '../api/types';
import {
  ApiMediaFormat,
} from '../api/types';

import {
  DEBUG, ELECTRON_HOST_URL,
  IS_PACKAGED_ELECTRON, MEDIA_CACHE_DISABLED, MEDIA_CACHE_NAME, MEDIA_CACHE_NAME_AVATARS,
  OSS_PREFETCH_TIMEOUT_MS,
} from '../config';
import { callApi, cancelApiProgress, forceReconnectApi } from '../api/gramjs';
import {
  IS_IOS, IS_OPUS_SUPPORTED, IS_PROGRESSIVE_SUPPORTED,
} from './browser/windowEnvironment';
import * as cacheApi from './cacheApi';
import { fetchBlob } from './files';
import { mediaDebugLog } from './mediaDebug';
import { registerMediaFetchAbortHandler } from './mediaFetchRegistry';
import { ACCOUNT_SLOT } from './multiaccount';
import { oggToWav } from './oggToWav';
import {
  buildOssMediaPathUnchecked,
  buildOssMediaUrl,
  clearOssMediaUnavailable,
  isAvatarOrProfileMediaHash,
  isOssMediaPath,
  isOssMediaUnavailable,
  isOssServingUrl,
  markOssMediaUnavailable,
  needsOssVoiceBlobFetch,
  stripClientMediaHashParams,
} from './ossMediaUrl';

const asCacheApiType = {
  [ApiMediaFormat.BlobUrl]: cacheApi.Type.Blob,
  [ApiMediaFormat.Text]: cacheApi.Type.Text,
  [ApiMediaFormat.DownloadUrl]: undefined,
  [ApiMediaFormat.Progressive]: undefined,
};

const PROGRESSIVE_URL_PREFIX = `${IS_PACKAGED_ELECTRON ? ELECTRON_HOST_URL : '.'}/progressive/`;
const DOWNLOAD_URL_PREFIX = './download/';
const MAX_MEDIA_RETRIES = 5;
const SESSION_RECONNECT_THRESHOLD = 2;

function isServiceWorkerControllerReady() {
  return Boolean(navigator.serviceWorker?.controller);
}

const MEMORY_CACHE_LIMIT = 300;
const MEMORY_CACHE_PRESSURE_LIMIT = 40;

const memoryCache = new Map<string, ApiPreparedMedia>();
const progressiveFallbackUrls = new Set<string>();
const fetchPromises = new Map<string, Promise<ApiPreparedMedia>>();
const fetchStartedAt = new Map<string, number>();
const progressCallbacks = new Map<string, Map<string, ApiOnProgress>>();
const cancellableCallbacks = new Map<string, ApiOnProgress>();
const sessionErrorCountsByUrl = new Map<string, number>();

type OssPrefetchResult = 'ok' | 'missing' | 'transient';

const ossPrefetchPromises = new Map<string, Promise<OssPrefetchResult>>();
const ossPrefetchFailedUrls = new Set<string>();

const ossErrorAttempts = new Map<string, number>();
const OSS_MAX_TRANSIENT_REMOUNTS = 2;

const ossRestoreRounds = new Map<string, number>();
const OSS_MAX_RESTORE_ROUNDS = 2;
const OSS_RESTORE_ROUNDS_LIMIT = 500;

const MAX_CONCURRENT_FALLBACKS = 3;
const FALLBACK_SLOT_MAX_HOLD_MS = 30000;
const FALLBACK_JITTER_MAX_MS = 2000;

type FallbackSlot = { acquired: boolean; timer?: number };

let activeFallbacks = 0;
const fallbackWaiters: Array<() => void> = [];
const fallbackHolders = new Map<string, FallbackSlot>();

const mediaInvalidationListeners = new Map<string, Set<() => void>>();

export function subscribeMediaInvalidation(mediaHash: string, callback: () => void) {
  let listeners = mediaInvalidationListeners.get(mediaHash);
  if (!listeners) {
    listeners = new Set();
    mediaInvalidationListeners.set(mediaHash, listeners);
  }
  listeners.add(callback);

  return () => {
    const current = mediaInvalidationListeners.get(mediaHash);
    if (!current) return;
    current.delete(callback);
    if (!current.size) {
      mediaInvalidationListeners.delete(mediaHash);
    }
  };
}

function notifyMediaInvalidated(mediaHash: string) {
  mediaInvalidationListeners.get(mediaHash)?.forEach((callback) => callback());
}

function acquireFallbackSlot(): Promise<void> {
  if (activeFallbacks < MAX_CONCURRENT_FALLBACKS) {
    activeFallbacks += 1;
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    fallbackWaiters.push(() => {
      activeFallbacks += 1;
      resolve();
    });
  });
}

function releaseFallbackSlot() {
  activeFallbacks = Math.max(0, activeFallbacks - 1);
  const next = fallbackWaiters.shift();
  next?.();
}

function releaseFallbackHash(mediaHash: string) {
  const slot = fallbackHolders.get(mediaHash);
  if (!slot) return;

  fallbackHolders.delete(mediaHash);
  if (slot.timer !== undefined) {
    clearTimeout(slot.timer);
  }
  if (slot.acquired) {
    releaseFallbackSlot();
  }
}

function getFallbackJitter(mediaHash: string) {
  let sum = 0;
  for (let i = 0; i < mediaHash.length; i++) {
    sum += mediaHash.charCodeAt(i);
  }

  return sum % FALLBACK_JITTER_MAX_MS;
}

async function admitFallback(mediaHash: string) {
  if (fallbackHolders.has(mediaHash)) {
    return;
  }

  const slot: FallbackSlot = { acquired: false };
  fallbackHolders.set(mediaHash, slot);

  await acquireFallbackSlot();

  if (fallbackHolders.get(mediaHash) !== slot) {
    releaseFallbackSlot();
    return;
  }

  slot.acquired = true;
  slot.timer = window.setTimeout(() => releaseFallbackHash(mediaHash), FALLBACK_SLOT_MAX_HOLD_MS);

  const jitter = getFallbackJitter(mediaHash);
  if (jitter) {
    await new Promise((resolve) => {
      setTimeout(resolve, jitter);
    });
  }
}

async function fallBackFromOssMedia(mediaHash: string, ossUrl: string) {
  if (!isOssMediaPath(ossUrl) || isOssMediaUnavailable(ossUrl)) {
    return;
  }

  await admitFallback(mediaHash);

  markOssMediaUnavailable(ossUrl);
  removeFromMemory(mediaHash);
  abortMediaFetch(mediaHash);

  if (!MEDIA_CACHE_DISABLED) {
    void cacheApi.remove(getOssCacheName(mediaHash), mediaHash);
  }

  mediaDebugLog('oss-missing-fallback', mediaHash, ossUrl);
}

export async function handleOssMediaError(mediaHash: string) {
  const ossUrl = buildOssMediaUrl(mediaHash);
  if (!ossUrl || !isOssMediaPath(ossUrl)) {
    return;
  }

  const attempt = (ossErrorAttempts.get(mediaHash) || 0) + 1;
  ossErrorAttempts.set(mediaHash, attempt);

  const result = await prefetchOssMediaToCache(mediaHash, ossUrl);

  if (result === 'missing' || attempt > OSS_MAX_TRANSIENT_REMOUNTS) {
    await fallBackFromOssMedia(mediaHash, ossUrl);
  } else {
    mediaDebugLog('oss-transient-remount', mediaHash, 'attempt:', attempt, 'result:', result);
  }

  notifyMediaInvalidated(mediaHash);
}

export function restoreOssMedia(mediaHash: string) {
  const ossUrl = buildOssMediaPathUnchecked(mediaHash);
  if (!ossUrl || !isOssMediaPath(ossUrl)) {
    return false;
  }

  const rounds = ossRestoreRounds.get(mediaHash) || 0;
  if (rounds >= OSS_MAX_RESTORE_ROUNDS) {
    return false;
  }

  if (ossRestoreRounds.size >= OSS_RESTORE_ROUNDS_LIMIT) {
    ossRestoreRounds.clear();
  }
  ossRestoreRounds.set(mediaHash, rounds + 1);

  clearOssMediaUnavailable(ossUrl);
  ossPrefetchFailedUrls.delete(mediaHash);
  ossErrorAttempts.delete(mediaHash);
  removeFromMemory(mediaHash);
  abortMediaFetch(mediaHash);

  mediaDebugLog('oss-restore', mediaHash, 'round:', rounds + 1);
  notifyMediaInvalidated(mediaHash);

  return true;
}

function isMemoryCacheProtectedValue(value: ApiPreparedMedia | undefined, key: string) {
  if (isAvatarOrProfileMediaHash(key)) {
    return true;
  }

  return typeof value === 'string' && isOssServingUrl(value);
}

function trimMemoryCacheTo(limit: number) {
  while (memoryCache.size > limit) {
    let oldestKey: string | undefined;

    for (const key of memoryCache.keys()) {
      const cached = memoryCache.get(key);
      if (!isMemoryCacheProtectedValue(cached, key)) {
        oldestKey = key;
        break;
      }
    }

    if (!oldestKey) {
      break;
    }

    const cached = memoryCache.get(oldestKey);
    memoryCache.delete(oldestKey);
    if (cached && typeof cached === 'string' && cached.startsWith('blob:')) {
      URL.revokeObjectURL(cached);
    }
  }
}

function setMemoryCache(url: string, prepared: ApiPreparedMedia) {
  memoryCache.set(url, prepared);
  trimMemoryCacheTo(MEMORY_CACHE_LIMIT);
}

export function releaseMemoryCacheUnderPressure() {
  const sizeBefore = memoryCache.size;
  trimMemoryCacheTo(MEMORY_CACHE_PRESSURE_LIMIT);
  return sizeBefore - memoryCache.size;
}

function upgradeProgressiveFallbackUrls() {
  if (!isServiceWorkerControllerReady() || !progressiveFallbackUrls.size) {
    return;
  }

  progressiveFallbackUrls.forEach((url) => {
    removeFromMemory(url);
    setMemoryCache(url, getProgressiveUrl(url));
  });

  progressiveFallbackUrls.clear();
}

export function fetch<T extends ApiMediaFormat>(
  url: string,
  mediaFormat: T,
  isHtmlAllowed = false,
  onProgress?: ApiOnProgress,
  callbackUniqueId?: string,
): Promise<ApiPreparedMedia> {
  const ossUrl = buildOssMediaUrl(url);
  if (ossUrl) {
    const cached = getFromMemory(url);
    if (cached && (!needsOssVoiceBlobFetch(url) || !isOssServingUrl(String(cached)))) {
      return Promise.resolve(cached);
    }

    if (onProgress && callbackUniqueId) {
      onProgress.isCanceled = false;

      let activeCallbacks = progressCallbacks.get(url);
      if (!activeCallbacks) {
        activeCallbacks = new Map<string, ApiOnProgress>();
        progressCallbacks.set(url, activeCallbacks);
      }
      activeCallbacks.set(callbackUniqueId, onProgress);
    }

    if (!fetchPromises.has(url)) {
      fetchStartedAt.set(url, Date.now());

      const fetchOss = needsOssVoiceBlobFetch(url) ? fetchOssVoiceMedia : fetchOssMedia;
      const promise = fetchOss(url, ossUrl)
        .catch((err) => {
          if (!(err instanceof Error && err.message === 'DOWNLOAD_ABORTED')) {
            // eslint-disable-next-line no-console
            console.warn('OSS media load error', url, err);
          }
          throw err;
        })
        .finally(() => {
          if (fetchPromises.get(url) === promise) {
            fetchPromises.delete(url);
            fetchStartedAt.delete(url);
            progressCallbacks.delete(url);
            cancellableCallbacks.delete(url);
          }
        });

      fetchPromises.set(url, promise);
    }

    return fetchPromises.get(url) as Promise<ApiPreparedMedia>;
  }

  if (mediaFormat === ApiMediaFormat.Progressive) {
    if (isServiceWorkerControllerReady()) {
      upgradeProgressiveFallbackUrls();
    }

    return (
      IS_PROGRESSIVE_SUPPORTED && isServiceWorkerControllerReady()
        ? Promise.resolve(getProgressiveUrl(url))
        : fetch(url, ApiMediaFormat.BlobUrl, isHtmlAllowed, onProgress, callbackUniqueId)
          .then((result) => {
            progressiveFallbackUrls.add(url);
            return result;
          })
    );
  }

  if (mediaFormat === ApiMediaFormat.DownloadUrl) {
    return (
      IS_PROGRESSIVE_SUPPORTED && isServiceWorkerControllerReady()
        ? Promise.resolve(getDownloadUrl(url))
        : fetch(url, ApiMediaFormat.BlobUrl, isHtmlAllowed, onProgress, callbackUniqueId)
    );
  }

  if (!fetchPromises.has(url)) {
    fetchStartedAt.set(url, Date.now());

    const promise = fetchFromCacheOrRemote(url, mediaFormat, isHtmlAllowed)
      .catch((err) => {
        if (isRetryableSessionError(err)) {
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.warn('media load transient error', url, err);
          }
        } else if (!(err instanceof Error && err.message === 'DOWNLOAD_ABORTED')) {
          if (err instanceof Error && err.message.startsWith('Failed to fetch media ')) {
            if (DEBUG && !isOssServingUrl(url)) {
              // eslint-disable-next-line no-console
              console.warn('media load fallback warning', url, err.message);
            }
          } else if (isFileReferenceMediaError(err) && isAvatarOrProfileUrl(url)) {
            if (DEBUG) {
              // eslint-disable-next-line no-console
              console.warn('media load file reference expired', url, err);
            }
          } else {
            // eslint-disable-next-line no-console
            console.error('media load error', url, err);
          }
        }
        throw err;
      })
      .finally(() => {
        releaseFallbackHash(url);

        if (fetchPromises.get(url) === promise) {
          fetchPromises.delete(url);
          fetchStartedAt.delete(url);
          progressCallbacks.delete(url);
          cancellableCallbacks.delete(url);
        }
      });

    fetchPromises.set(url, promise);
  }

  if (onProgress && callbackUniqueId) {
    onProgress.isCanceled = false;

    let activeCallbacks = progressCallbacks.get(url);
    if (!activeCallbacks) {
      activeCallbacks = new Map<string, ApiOnProgress>();
      progressCallbacks.set(url, activeCallbacks);
    }
    activeCallbacks.set(callbackUniqueId, onProgress);
  }

  return fetchPromises.get(url) as Promise<ApiPreparedMedia>;
}

export function getFromMemory(url: string) {
  return memoryCache.get(url) as ApiPreparedMedia;
}

export function getPreparedMedia(url: string) {
  if (needsOssVoiceBlobFetch(url)) {
    const cached = getFromMemory(url);
    return cached && !isOssServingUrl(String(cached)) ? cached : undefined;
  }

  const ossUrl = buildOssMediaUrl(url);
  if (ossUrl) {
    return ossUrl;
  }

  return getFromMemory(url);
}

export function hasPendingFetch(url: string) {
  return fetchPromises.has(url);
}

export function getPendingFetchAge(url: string) {
  const startedAt = fetchStartedAt.get(url);
  return startedAt ? Date.now() - startedAt : 0;
}

export function removeFromMemory(url: string) {
  const cached = memoryCache.get(url);
  memoryCache.delete(url);
  if (cached && cached.startsWith('blob:')) {
    setTimeout(() => {
      URL.revokeObjectURL(cached);
    }, 1000);
  }
}

export function cancelProgress(progressCallback: ApiOnProgress) {
  progressCallbacks.forEach((map, url) => {
    let hasRemoved = false;

    map.forEach((callback, callbackId) => {
      if (callback === progressCallback) {
        map.delete(callbackId);
        hasRemoved = true;
      }
    });

    if (!hasRemoved) return;

    if (!map.size) {
      const parentCallback = cancellableCallbacks.get(url);
      if (!parentCallback) return;

      cancelApiProgress(parentCallback);
      cancellableCallbacks.delete(url);
      progressCallbacks.delete(url);
    }
  });
}

export function abortMediaFetch(url: string) {
  const parentCallback = cancellableCallbacks.get(url);
  if (parentCallback) {
    cancelApiProgress(parentCallback);
    cancellableCallbacks.delete(url);
  }
  fetchPromises.delete(url);
  fetchStartedAt.delete(url);
  progressCallbacks.delete(url);
}

export function abortAllMediaFetches() {
  [...fetchPromises.keys()].forEach(abortMediaFetch);
}

registerMediaFetchAbortHandler(abortAllMediaFetches);

export function removeCallback(url: string, callbackUniqueId: string) {
  const callbacks = progressCallbacks.get(url);
  if (!callbacks) return;
  callbacks.delete(callbackUniqueId);
}

function getProgressiveUrl(url: string) {
  const normalizedUrl = normalizeMediaUrl(url);
  const base = new URL(`${PROGRESSIVE_URL_PREFIX}${normalizedUrl}`, window.location.href);
  if (ACCOUNT_SLOT) base.searchParams.set('account', ACCOUNT_SLOT.toString());
  setMemoryCache(url, base.href);
  return base.href;
}

function getDownloadUrl(url: string) {
  const normalizedUrl = normalizeMediaUrl(url);
  const base = new URL(`${DOWNLOAD_URL_PREFIX}${normalizedUrl}`, window.location.href);
  if (ACCOUNT_SLOT) base.searchParams.set('account', ACCOUNT_SLOT.toString());
  return base.href;
}

function normalizeMediaUrl(url: string) {
  if (!url) return url;

  if (/^https?:\/\//.test(url)) {
    try {
      const parsed = new URL(url);
      const pathname = parsed.pathname.startsWith('/') ? parsed.pathname.slice(1) : parsed.pathname;
      return `${pathname}${parsed.search}`;
    } catch {
      return url;
    }
  }

  return url.startsWith('/') ? url.slice(1) : url;
}

function getOssCacheName(url: string) {
  return url.startsWith('avatar') ? MEDIA_CACHE_NAME_AVATARS : MEDIA_CACHE_NAME;
}

async function fetchOssMedia(url: string, ossUrl: string): Promise<ApiPreparedMedia> {
  const existing = getFromMemory(url);
  if (existing) {
    return existing;
  }

  const onProgress = makeOnProgress(url);
  cancellableCallbacks.set(url, onProgress);

  if (!MEDIA_CACHE_DISABLED) {
    const cached = await cacheApi.fetch(getOssCacheName(url), url, cacheApi.Type.Blob);
    if (cached) {
      const prepared = prepareMedia(cached);
      setMemoryCache(url, prepared);
      onProgress(1);
      return prepared;
    }
  }

  if (onProgress.isCanceled) {
    throw new Error('DOWNLOAD_ABORTED');
  }

  onProgress(1);
  setMemoryCache(url, ossUrl);

  if (!MEDIA_CACHE_DISABLED && !ossPrefetchFailedUrls.has(url)) {
    void prefetchOssMediaToCache(url, ossUrl).then((result) => {
      if (result === 'ok') {
        ossRestoreRounds.delete(url);
        return;
      }

      if (result !== 'missing') {
        return;
      }

      void fallBackFromOssMedia(url, ossUrl).then(() => {
        notifyMediaInvalidated(url);
      });
    });
  }

  return ossUrl;
}

async function fetchOssVoiceMedia(url: string, ossUrl: string): Promise<ApiPreparedMedia> {
  const existing = getFromMemory(url);
  if (existing) {
    if (isOssServingUrl(String(existing))) {
      removeFromMemory(url);
    } else {
      return existing;
    }
  }

  const onProgress = makeOnProgress(url);
  cancellableCallbacks.set(url, onProgress);

  if (!MEDIA_CACHE_DISABLED) {
    const cached = await cacheApi.fetch(getOssCacheName(url), url, cacheApi.Type.Blob);
    if (cached) {
      try {
        const media = await prepareOssVoiceForPlayback(cached);
        const prepared = prepareMedia(media);
        setMemoryCache(url, prepared);
        onProgress(1);
        return prepared;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('OSS voice cache decode failed, refetching', url, err);
      }
    }
  }

  if (onProgress.isCanceled) {
    throw new Error('DOWNLOAD_ABORTED');
  }

  try {
    onProgress(0);
    const response = await globalThis.fetch(ossUrl);
    if (!response.ok || onProgress.isCanceled) {
      throw new Error(`OSS voice fetch failed: ${response.status}`);
    }

    const blob = await response.blob();
    if (onProgress.isCanceled) {
      throw new Error('DOWNLOAD_ABORTED');
    }

    const media = await prepareOssVoiceForPlayback(blob);
    const prepared = prepareMedia(media);
    setMemoryCache(url, prepared);
    onProgress(1);

    if (!MEDIA_CACHE_DISABLED) {
      void cacheApi.save(getOssCacheName(url), url, media);
    }

    return prepared;
  } catch (err) {
    if (err instanceof Error && err.message === 'DOWNLOAD_ABORTED') {
      throw err;
    }

    removeFromMemory(url);
    const mtprotoUrl = stripClientMediaHashParams(url);
    // eslint-disable-next-line no-console
    console.warn('OSS voice load failed, falling back to MTProto', url, err);
    return fetchFromCacheOrRemote(mtprotoUrl, ApiMediaFormat.BlobUrl, false);
  }
}

const OSS_VOICE_DECODE_TIMEOUT_MS = 20000;

async function prepareOssVoiceForPlayback(blob: Blob): Promise<Blob> {
  const media = normalizeOssVoiceBlob(blob);

  if (media.type.includes('wav') || media.type.includes('mpeg') || media.type.includes('mp4')) {
    return media;
  }

  if (IS_IOS || !IS_OPUS_SUPPORTED) {
    return withTimeout(oggToWav(media), OSS_VOICE_DECODE_TIMEOUT_MS, 'OSS voice decode timeout');
  }

  return media;
}

function normalizeOssVoiceBlob(blob: Blob) {
  const type = blob.type || '';
  if (type.startsWith('audio/')) {
    return blob;
  }

  return new Blob([blob], { type: 'audio/ogg; codecs=opus' });
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function prefetchOssMediaToCache(url: string, ossUrl: string): Promise<OssPrefetchResult> {
  if (ossPrefetchFailedUrls.has(url)) {
    return Promise.resolve('missing');
  }

  let prefetch = ossPrefetchPromises.get(url);
  if (!prefetch) {
    prefetch = (async (): Promise<OssPrefetchResult> => {
      const onProgress = cancellableCallbacks.get(url);
      if (onProgress?.isCanceled) {
        return 'transient';
      }

      const controller = new AbortController();
      const abortTimer = window.setTimeout(() => controller.abort(), OSS_PREFETCH_TIMEOUT_MS);

      try {
        const response = await globalThis.fetch(ossUrl, { signal: controller.signal });
        if (!response.ok) {
          if (response.status === 404 || response.status === 403) {
            ossPrefetchFailedUrls.add(url);
            return 'missing';
          }

          return 'transient';
        }

        if (onProgress?.isCanceled) {
          return 'transient';
        }

        const blob = await response.blob();
        if (onProgress?.isCanceled) {
          return 'transient';
        }

        try {
          await cacheApi.save(getOssCacheName(url), url, blob);
        } catch {
        }

        return 'ok';
      } catch {
        return 'transient';
      } finally {
        window.clearTimeout(abortTimer);
      }
    })().finally(() => {
      ossPrefetchPromises.delete(url);
    });

    ossPrefetchPromises.set(url, prefetch);
  }

  return prefetch;
}

async function fetchFromCacheOrRemote(
  url: string, mediaFormat: ApiMediaFormat, isHtmlAllowed: boolean, retryNumber = 0,
): Promise<string> {
  if (!MEDIA_CACHE_DISABLED) {
    const cacheName = url.startsWith('avatar') ? MEDIA_CACHE_NAME_AVATARS : MEDIA_CACHE_NAME;
    const cached = await cacheApi.fetch(cacheName, url, asCacheApiType[mediaFormat]!, isHtmlAllowed);

    if (cached) {
      let media = cached;

      if (cached.type === 'audio/ogg' && !IS_OPUS_SUPPORTED) {
        media = await oggToWav(media);
      }

      const prepared = prepareMedia(media);

      setMemoryCache(url, prepared);

      return prepared;
    }
  }

  const onProgress = makeOnProgress(url);
  cancellableCallbacks.set(url, onProgress);

  let remote;
  try {
    remote = await callApi('downloadMedia', { url, mediaFormat, isHtmlAllowed }, onProgress);
  } catch (err) {
    const errMessage = err instanceof Error ? err.message : String(err);
    const isQueueTimeout = errMessage === 'MEDIA_QUEUE_TIMEOUT';

    if ((isRetryableSessionError(err) || isQueueTimeout) && retryNumber < MAX_MEDIA_RETRIES) {
      if (onProgress.isCanceled) {
        throw new Error('DOWNLOAD_ABORTED');
      }

      const sessionErrorCount = (sessionErrorCountsByUrl.get(url) || 0) + 1;
      sessionErrorCountsByUrl.set(url, sessionErrorCount);

      if (sessionErrorCount === SESSION_RECONNECT_THRESHOLD) {
        abortAllMediaFetches();
        forceReconnectApi('media-session-error');
      }

      await new Promise((resolve) => {
        setTimeout(resolve, getRetryTimeout(retryNumber));
      });
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.debug(`Retrying media after transient session error ${url}`);
      }
      return fetchFromCacheOrRemote(url, mediaFormat, isHtmlAllowed, retryNumber + 1);
    }

    throw err;
  }

  sessionErrorCountsByUrl.delete(url);

  if (!remote) {
    if (onProgress.isCanceled) {
      throw new Error('DOWNLOAD_ABORTED');
    }

    if (shouldSkipRetryWithoutRemote(url)) {
      return undefined as unknown as string;
    }

    if (retryNumber >= MAX_MEDIA_RETRIES) {
      throw new Error(`Failed to fetch media ${url}`);
    }
    await new Promise((resolve) => {
      setTimeout(resolve, getRetryTimeout(retryNumber));
    });
    // eslint-disable-next-line no-console
    if (DEBUG) console.debug(`Retrying to fetch media ${url}`);
    return fetchFromCacheOrRemote(url, mediaFormat, isHtmlAllowed, retryNumber + 1);
  }

  const { mimeType } = remote;
  let prepared = prepareMedia(remote.dataBlob);

  if (mimeType === 'audio/ogg' && !IS_OPUS_SUPPORTED) {
    const blob = await fetchBlob(prepared as string);
    URL.revokeObjectURL(prepared as string);
    const media = await oggToWav(blob);
    prepared = prepareMedia(media);
  }

  setMemoryCache(url, prepared);

  if (!MEDIA_CACHE_DISABLED && remote.dataBlob instanceof Blob) {
    const cacheName = url.startsWith('avatar') ? MEDIA_CACHE_NAME_AVATARS : MEDIA_CACHE_NAME;
    void cacheApi.save(cacheName, url, remote.dataBlob);
  }

  return prepared;
}

function makeOnProgress(url: string) {
  const onProgress: ApiOnProgress = (progress: number) => {
    progressCallbacks.get(url)?.forEach((callback) => {
      callback(progress);
      if (callback.isCanceled) onProgress.isCanceled = true;
    });
  };

  return onProgress;
}

function prepareMedia(mediaData: Exclude<ApiParsedMedia, ArrayBuffer>): ApiPreparedMedia {
  if (mediaData instanceof Blob) {
    return URL.createObjectURL(mediaData);
  }

  return mediaData;
}

if (IS_PROGRESSIVE_SUPPORTED) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    upgradeProgressiveFallbackUrls();
  });

  navigator.serviceWorker.addEventListener('message', async (e) => {
    const { type, messageId, params } = e.data as {
      type: string;
      messageId: string;
      params: { url: string; start: number; end: number };
    };

    if (type !== 'requestPart') {
      return;
    }

    const target = (e.source as ServiceWorker | null) || navigator.serviceWorker.controller;
    if (!target) {
      return;
    }

    try {
      const result = await callApi('downloadMedia', { mediaFormat: ApiMediaFormat.Progressive, ...params });
      const { arrayBuffer, mimeType, fullSize } = result || {};

      if (!arrayBuffer || !mimeType || !fullSize) {
        target.postMessage({
          type: 'partResponse',
          messageId,
          error: 'ERROR_EMPTY_PART',
        });
        return;
      }

      target.postMessage({
        type: 'partResponse',
        messageId,
        result: {
          arrayBuffer,
          mimeType,
          fullSize,
        },
      }, [arrayBuffer]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      target.postMessage({
        type: 'partResponse',
        messageId,
        error: message,
      });
    }
  });
}

function getRetryTimeout(retryNumber: number) {
  return 250 * 2 ** retryNumber;
}

function isRetryableSessionError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message === 'SESSION_REVOKED'
    || message === 'DOWNLOAD_TIMEOUT_ALT_DC'
    || message === 'CONNECTION_NOT_INITED';
}

function isFileReferenceMediaError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes('FILE_REFERENCE');
}

function isAvatarOrProfileUrl(url: string) {
  return url.startsWith('avatar') || url.startsWith('profile');
}

function shouldSkipRetryWithoutRemote(url: string) {
  return isAvatarOrProfileUrl(url) || url.startsWith('photo');
}
